import { ScanOutlined } from '@ant-design/icons';
import type { DeliveryItemRow, DeliveryRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatQty } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  InputNumber,
  Space,
  Spin,
  Table,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

interface SignLineState {
  deliveryItemId: string;
  signQty: number;
  rejectQty: number;
}

/** 扫码签收：输入配送单 → 逐行确认实收 → 上传签名影像 → 提交（演-3）。 */
export default function SignScan() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [deliveryId, setDeliveryId] = useState('');
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [lines, setLines] = useState<Record<string, SignLineState>>({});
  const [scanCode, setScanCode] = useState('');
  const [exceptionDesc, setExceptionDesc] = useState('');
  const [signatureImg, setSignatureImg] = useState('');
  const [geoPoint, setGeoPoint] = useState('');

  const detail = useQuery({
    queryKey: ['delivery', 'detail', submittedId],
    queryFn: () => hq.delivery.detail(submittedId ?? ''),
    enabled: Boolean(submittedId)
  });

  // 扫码解析：把 PS 单号 / 二维码原文归一成配送单主键（未识别不抛错）
  const resolve = useMutation({
    mutationFn: (code: string) => hq.scan.resolve({ code, terminal: 'H5' }),
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '二维码解析失败')
  });

  const sign = useMutation({
    mutationFn: () =>
      hq.delivery.sign(submittedId ?? '', {
        items: Object.values(lines)
          .filter((line) => line.signQty > 0 || line.rejectQty > 0)
          .map((line) => ({
            deliveryItemId: line.deliveryItemId,
            signQty: line.signQty,
            rejectQty: line.rejectQty
          })),
        scanCode: scanCode || undefined,
        exceptionDesc: exceptionDesc || undefined,
        signatureImg: signatureImg || undefined,
        geoPoint: geoPoint || undefined,
        signMethod: 'SCAN_QR',
        attachments: signatureImg
          ? [{ fileUrl: signatureImg, fileType: 'SIGNATURE' }]
          : undefined
      }),
    onSuccess: async (result) => {
      message.success(`签收成功：${result.signNo}`);
      await queryClient.invalidateQueries({ queryKey: ['delivery'] });
      await queryClient.invalidateQueries({ queryKey: ['sign'] });
      setLines({});
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '签收失败')
  });

  /**
   * 扫码值通常是 `PS...` 单号而不是配送单主键（后端路由是 `{id:long}`），
   * 因此非纯数字输入先走 `/wl/scan/resolve` 归一成 bizId，再加载详情。
   */
  const loadDetail = async () => {
    const raw = deliveryId.trim();
    if (!raw) {
      message.warning('请输入或扫描配送单号');
      return;
    }
    if (/^\d+$/.test(raw)) {
      setSubmittedId(raw);
      return;
    }
    const resolved = await resolve.mutateAsync(raw);
    if (!resolved.matched) {
      message.warning(resolved.message ?? '未识别的二维码');
      return;
    }
    if (resolved.scanType !== 'DELIVERY') {
      const action = resolved.nextActions[0];
      message.info(
        `识别为${resolved.scanType}${resolved.bizNo ? `：${resolved.bizNo}` : ''}${action ? `，请前往「${action.label}」` : ''}`
      );
      if (action?.route) navigate(action.route);
      return;
    }
    setSubmittedId(resolved.bizId);
  };

  const delivery: DeliveryRow | undefined = detail.data?.delivery;
  const items: DeliveryItemRow[] = detail.data?.items ?? [];

  // 明细加载后初始化签收行：默认按「应收 - 已签」带出，用户已改动的行保持原值
  useEffect(() => {
    if (items.length === 0) return;
    setLines((prev) => {
      const next: Record<string, SignLineState> = {};
      for (const item of items) {
        next[item.id] = prev[item.id] ?? {
          deliveryItemId: item.id,
          signQty: Math.max(0, item.planQty - item.signQty),
          rejectQty: 0
        };
      }
      return next;
    });
  }, [items]);

  const columns: ColumnsType<DeliveryItemRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 140, render: (v: string | null) => v || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 130, render: (v: string | null) => v || '-' },
    { title: '应收', dataIndex: 'planQty', width: 90, align: 'right', render: (v: number) => formatQty(v) },
    {
      title: '本次实收',
      width: 140,
      align: 'right',
      render: (_, row) => (
        <InputNumber
          min={0}
          max={row.planQty - row.signQty}
          step={1}
          value={lines[row.id]?.signQty ?? 0}
          onChange={(value) =>
            setLines((prev) => {
              const current = prev[row.id] ?? { deliveryItemId: row.id, signQty: 0, rejectQty: 0 };
              return { ...prev, [row.id]: { ...current, signQty: Number(value ?? 0) } };
            })
          }
        />
      )
    },
    {
      title: '拒收',
      width: 140,
      align: 'right',
      render: (_, row) => (
        <InputNumber
          min={0}
          max={row.planQty - row.signQty}
          step={1}
          value={lines[row.id]?.rejectQty ?? 0}
          onChange={(value) =>
            setLines((prev) => {
              const current = prev[row.id] ?? { deliveryItemId: row.id, signQty: 0, rejectQty: 0 };
              return { ...prev, [row.id]: { ...current, rejectQty: Number(value ?? 0) } };
            })
          }
        />
      )
    }
  ];

  const canSign = delivery ? !['SIGNED', 'REJECTED', 'CANCELED', 'LOST'].includes(delivery.deliveryStatus) : false;

  return (
    <Card className="page-card" title="扫码签收">
      <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
        <Input
          prefix={<ScanOutlined />}
          placeholder="扫描配送单二维码（PS 单号），或输入配送单 ID"
          value={deliveryId}
          onChange={(event) => setDeliveryId(event.target.value)}
          onPressEnter={() => void loadDetail()}
        />
        <Button type="primary" onClick={() => void loadDetail()} loading={resolve.isPending || detail.isFetching}>
          查询
        </Button>
      </Space.Compact>

      {detail.isError ? (
        <Alert
          type="error"
          showIcon
          message="配送单加载失败"
          description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
        />
      ) : null}

      {detail.isLoading ? <Spin /> : null}

      {!submittedId ? (
        <Empty description="请先扫描或输入配送单" />
      ) : delivery ? (
        <>
          <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="配送单号">{delivery.bizNo}</Descriptions.Item>
            <Descriptions.Item label="收货科室">{delivery.toDeptName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="状态">{delivery.deliveryStatus}</Descriptions.Item>
            <Descriptions.Item label="配送人">{delivery.deliverName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="配送类型">{delivery.deliveryType}</Descriptions.Item>
            <Descriptions.Item label="收货位置">{delivery.toSpaceDesc ?? '-'}</Descriptions.Item>
          </Descriptions>

          <Table<DeliveryItemRow>
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={items}
            pagination={false}
            scroll={{ x: sumWidth(columns) }}
          />

          <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
            <Input
              addonBefore="签名图 URL"
              placeholder="资源服务返回的签名图片地址（可选）"
              value={signatureImg}
              onChange={(event) => setSignatureImg(event.target.value)}
            />
            <Input
              addonBefore="定位"
              placeholder="经纬度，如 120.15,30.28（可选）"
              value={geoPoint}
              onChange={(event) => setGeoPoint(event.target.value)}
            />
            <Input
              addonBefore="异常说明"
              placeholder="有拒收或短少时填写"
              value={exceptionDesc}
              onChange={(event) => setExceptionDesc(event.target.value)}
            />
            <Input
              addonBefore="扫码值"
              placeholder="被扫描的二维码内容（可选）"
              value={scanCode}
              onChange={(event) => setScanCode(event.target.value)}
            />
          </Space>

          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              disabled={!canSign}
              loading={sign.isPending}
              onClick={() => sign.mutate()}
            >
              确认签收
            </Button>
            <Typography.Text type="secondary">
              签收即出库：院内配送会同时生成出库单并扣减库存。
            </Typography.Text>
          </Space>
        </>
      ) : null}
    </Card>
  );
}
